import { useEffect, useFeatureIsOn, useIsGrowthbookReady } from '@frontend/react';
import { LoadingSpinnerPanel, MantineProvider, ModalsProvider, Notifications, SSOProvider } from '@frontend/web-react';
import { identifyClient } from '@frontend/web-utils';
import { RouterProvider } from '@frontend/web/components';
import {
  useAuthentication,
  useLazyRetrieveClientQuery,
  useLazyRetrieveClientStatusQuery,
  useRetrieveClientQuery,
  useRetrieveClientStatusQuery,
} from '@frontend/web/hooks';
import { mantineTheme } from '@frontend/web/mantine';
import { persistor, store } from '@frontend/web/redux/store';
import { router } from '@frontend/web/router';
import { Env, isMembershipComplete, isProfileComplete } from '@frontend/web/utils';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
// Core always needs to be first
import { useAuthSlice } from '@frontend/redux';
import '@mantine/carousel/styles.layer.css';
import '@mantine/core/styles.layer.css';
import '@mantine/dates/styles.layer.css';
import '@mantine/dropzone/styles.layer.css';
import '@mantine/notifications/styles.layer.css';
import '@mantine/spotlight/styles.layer.css';
import { lazy, Suspense, useState } from 'react';
import ReactDOM from 'react-dom/client';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { initApp } from './utils/initApp';

const { growthbook, segmentClient } = initApp();

const isAgentationEnabled = Env.VITE_ENV === 'development' || Env.VITE_ENV === 'local';

const Agentation = isAgentationEnabled
  ? lazy(() =>
      import('agentation').then((res) => ({
        default: res.Agentation,
      })),
    )
  : () => null;

// Needs to be wrapped inside Redux Provider/PersistGate before useAuthentication is accessible
export const Router = () => {
  const authentication = useAuthentication();
  const { ready: isGrowthbookReady } = useIsGrowthbookReady();
  const { enabled: isSignupChangesEnabled } = useFeatureIsOn('is_signup_changes_enabled');
  const {
    data: client,
    isFetching: isClientFetching,
    isLoading: isClientLoading,
  } = useRetrieveClientQuery(undefined, {
    skip: !authentication.isAuthenticated,
  });
  const {
    data: clientStatus,
    isFetching: isClientStatusFetching,
    isLoading: isClientStatusLoading,
  } = useRetrieveClientStatusQuery(undefined, {
    skip: !authentication.isAuthenticated,
  });

  const redirectContext = {
    isClientLoading: authentication.isAuthenticated ? isClientLoading || isClientFetching : false,
    isClientStatusLoading: authentication.isAuthenticated ? isClientStatusLoading || isClientStatusFetching : false,
    isGrowthbookReady,
    isSignupChangesEnabled,
    membershipComplete: isMembershipComplete({ client, clientStatus }),
    onboardingComplete: Boolean(clientStatus?.onboardingComplete),
    profileComplete: isProfileComplete(client),
  };

  return (
    <Suspense fallback={<div />}>
      <RouterProvider context={{ authentication, redirectContext }} router={router} />
    </Suspense>
  );
};

export const App = () => {
  const [retrieveClient, { isLoading: isClientLoading }] = useLazyRetrieveClientQuery();
  const [retrieveClientStatus] = useLazyRetrieveClientStatusQuery();
  const { client: reduxClient, storeClient, token } = useAuthSlice();
  const [isIdentifyDone, setIsIdentifyDone] = useState(false);
  useEffect(() => {
    if (token) {
      (async () => {
        await retrieveClient().then(
          async ({ data: client }) => {
            if (client) {
              storeClient(client);
            }

            // At runtime the client isn't immediately available and causes sentry errors
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (client || reduxClient) {
              const clientStatus = await retrieveClientStatus()
                .unwrap()
                .catch(() => undefined);
              identifyClient(client ?? reduxClient!, Env.VITE_ENV, clientStatus);
              setIsIdentifyDone(true);
            } else {
              setIsIdentifyDone(false);
            }
          },
          () => setIsIdentifyDone(true),
        );
      })();
    } else {
      setIsIdentifyDone(false);
      segmentClient?.ready(() => {
        growthbook.setAttributes({
          anonymousId: segmentClient.user().then((user) => user.anonymousId),
        });
      });
    }
    growthbook.refreshFeatures();
  }, [token]);

  // We want to wait until the client is identified with Growthbook, if they're logged in
  const isAppReadyToRender = !isClientLoading && (!token || isIdentifyDone);

  return (
    <>
      {!isAppReadyToRender ? <LoadingSpinnerPanel className="h-screen w-full pb-xxxl" fullScreen /> : null}
      <Router />
    </>
  );
};

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const rootElement = document.getElementById('root')!;
if (!rootElement?.innerHTML) {
  const { VITE_CLERK_PUBLISHABLE_KEY } = Env;

  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <>
      <MantineProvider theme={mantineTheme}>
        <div className="min-h-screen">
          <ModalsProvider>
            <Notifications position="bottom-left" />
            <SSOProvider publishableKey={VITE_CLERK_PUBLISHABLE_KEY}>
              <Provider store={store}>
                <PersistGate loading={null} persistor={persistor}>
                  <GrowthBookProvider growthbook={growthbook}>
                    <App />
                  </GrowthBookProvider>
                </PersistGate>
              </Provider>
            </SSOProvider>
            {/* <TanStackRouterDevtools initialIsOpen={false} router={router} /> */}
          </ModalsProvider>
        </div>
      </MantineProvider>
      <Suspense fallback={null}>
        <Agentation />
      </Suspense>
    </>,
  );
}
